import React, { useState, useEffect, useCallback } from 'react';
import { notificationsAPI, cardsAPI, customersAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Bell, Plus, Loader2, Send, Users, CreditCard, User, Smartphone, CheckCheck, AlertCircle, RefreshCw } from 'lucide-react';

const EMPTY_FORM = { title: '', message: '', target_type: 'all', card_id: '', target_id: '' };

// Promo message length cap. Mirror of backend PROMO_MAX_LENGTH
// (backend/routes/notifications.py:24). Keep both in sync.
const PROMO_MSG_MAX = 150;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [cards, setCards] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [previewCount, setPreviewCount] = useState({ customer_count: 0, device_count: 0 });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [quota, setQuota] = useState({ used_today: 0, max_per_day: 2, remaining: 2, next_reset_at: null });

  const fetchQuota = useCallback(async () => {
    try {
      const { data } = await notificationsAPI.getQuota();
      setQuota(data);
    } catch { /* ignore */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [notifRes, cardsRes, custRes] = await Promise.all([
        notificationsAPI.list(50),
        cardsAPI.list(),
        customersAPI.list({ limit: 100 }),
      ]);
      setNotifications(notifRes.data);
      setCards(cardsRes.data);
      setCustomers(custRes.data?.customers || []);
      fetchQuota();
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [fetchQuota]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live destinataires count
  useEffect(() => {
    if (!dialogOpen) return;
    const valid =
      (formData.target_type === 'all') ||
      (formData.target_type === 'card' && formData.card_id) ||
      (formData.target_type === 'customer' && formData.target_id);
    if (!valid) { setPreviewCount({ customer_count: 0, device_count: 0 }); return; }

    const t = setTimeout(async () => {
      try {
        const { data } = await notificationsAPI.previewCount({
          target_type: formData.target_type,
          card_id: formData.card_id || null,
          target_id: formData.target_id || null,
        });
        setPreviewCount(data);
      } catch {
        setPreviewCount({ customer_count: 0, device_count: 0 });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [dialogOpen, formData.target_type, formData.card_id, formData.target_id]);

  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setPreviewCount({ customer_count: 0, device_count: 0 });
    setDialogOpen(true);
  };

  const validate = () => {
    if (!formData.title.trim()) { toast.error('Titre requis'); return false; }
    if (!formData.message.trim()) { toast.error('Message requis'); return false; }
    if (formData.target_type === 'card' && !formData.card_id) { toast.error('Choisissez une carte'); return false; }
    if (formData.target_type === 'customer' && !formData.target_id) { toast.error('Choisissez un client'); return false; }
    return true;
  };

  const handleTestSelf = async () => {
    if (!validate()) return;
    setTesting(true);
    try {
      const { data } = await notificationsAPI.test({ title: formData.title, message: formData.message });
      toast.success(`Notification test envoyée à ${data.sent_count} appareil${data.sent_count > 1 ? 's' : ''}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'no_pass_in_wallet') {
        toast.error(detail.message, {
          duration: 12000,
          action: {
            label: 'Voir le QR boutique',
            onClick: () => navigate(detail.qr_url || '/dashboard/store-qr'),
          },
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : "Échec du test");
      }
    } finally {
      setTesting(false);
    }
  };

  const handleAskConfirmSend = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const payload = {
        title: formData.title,
        message: formData.message,
        target_type: formData.target_type,
        card_id: formData.card_id || null,
        target_id: formData.target_id || null,
      };
      const { data } = await notificationsAPI.create(payload);
      if (data.total_devices === 0) {
        toast.success("Notification enregistrée. Vous pourrez la renvoyer dès qu'un client aura ajouté votre carte à son wallet.", { duration: 8000 });
      } else {
        toast.success(`Envoyé : ${data.sent_count} • Échecs : ${data.failed_count}`);
      }
      setDialogOpen(false);
      setFormData(EMPTY_FORM);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && typeof detail === 'object') {
        toast.error(detail.message || 'Quota journalier atteint', { duration: 10000 });
        fetchQuota();
      } else {
        toast.error(typeof detail === 'string' ? detail : "Échec de l'envoi");
      }
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (notif) => {
    setResendingId(notif.id);
    try {
      const { data } = await notificationsAPI.resend(notif.id);
      if (data.device_count === 0) {
        toast.warning("Toujours aucun appareil cible. Vos clients doivent ajouter la carte à leur wallet.");
      } else {
        toast.success(`Renvoyé : ${data.sent_count} appareil${data.sent_count > 1 ? 's' : ''}${data.failed_count ? ` • ${data.failed_count} échec(s)` : ''}`);
      }
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && typeof detail === 'object') {
        toast.error(detail.message || 'Quota journalier atteint', { duration: 10000 });
        fetchQuota();
      } else {
        toast.error(typeof detail === 'string' ? detail : "Échec du renvoi");
      }
    } finally {
      setResendingId(null);
    }
  };

  const targetIcon = (type) => {
    if (type === 'all') return <Users className="w-3.5 h-3.5" />;
    if (type === 'card') return <CreditCard className="w-3.5 h-3.5" />;
    return <User className="w-3.5 h-3.5" />;
  };
  const targetLabel = (notif) => {
    if (notif.target_type === 'all') return 'Tous mes clients';
    if (notif.target_type === 'card') return cards.find(c => c.id === notif.card_id)?.name || 'Carte';
    if (notif.target_type === 'customer') return customers.find(c => c.id === notif.target_id)?.name || 'Client';
    return '—';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Notifications push
          </h1>
          <p className="text-slate-600 mt-1 max-w-2xl">
            Envoyez des messages à vos clients qui ont ajouté votre carte à leur wallet.
          </p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={openCreate}
          disabled={quota.remaining <= 0}
          data-testid="create-notification-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nouvelle notification
        </Button>
      </div>

      {/* Quota banner */}
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
          quota.remaining <= 0
            ? 'bg-amber-50 border-amber-200 text-amber-900'
            : 'bg-slate-50 border-slate-200 text-slate-700'
        }`}
        data-testid="quota-banner"
      >
        <Bell className={`w-4 h-4 shrink-0 ${quota.remaining <= 0 ? 'text-amber-600' : 'text-slate-500'}`} />
        <div className="text-sm flex-1">
          <span className="font-semibold">
            {quota.used_today}/{quota.max_per_day}
          </span>{' '}
          notifications envoyées aujourd'hui
          {quota.remaining <= 0 && quota.next_reset_at && (
            <span className="block text-xs mt-0.5">
              Prochaine notification disponible le{' '}
              {new Date(quota.next_reset_at).toLocaleString('fr-FR', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          )}
        </div>
        <Badge variant={quota.remaining <= 0 ? 'destructive' : 'outline'} className="text-[11px]">
          {quota.remaining} restante{quota.remaining > 1 ? 's' : ''}
        </Badge>
      </div>

      {/* History */}
      {notifications.length > 0 ? (
        <div className="space-y-3">
          {notifications.map((n) => (
            <Card key={n.id} className="border-slate-200" data-testid={`notification-${n.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                    <Bell className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <h3 className="font-semibold text-slate-900">{n.title}</h3>
                      <Badge variant="outline" className="gap-1 text-[11px] font-normal">
                        {targetIcon(n.target_type)} {targetLabel(n)}
                      </Badge>
                    </div>
                    <p className="text-slate-600 mt-1 text-sm leading-snug line-clamp-2">{n.message}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                        <CheckCheck className="w-3.5 h-3.5" /> {n.sent_count ?? 0} envoyé{(n.sent_count ?? 0) > 1 ? 's' : ''}
                      </span>
                      {(n.failed_count ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                          <AlertCircle className="w-3.5 h-3.5" /> {n.failed_count} échec{n.failed_count > 1 ? 's' : ''}
                        </span>
                      )}
                      <span>•</span>
                      <span>{new Date(n.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleResend(n)}
                    disabled={resendingId === n.id}
                    className="shrink-0"
                    data-testid={`resend-${n.id}`}
                  >
                    {resendingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Renvoyer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Bell className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune notification envoyée</h3>
            <p className="text-slate-500 text-center mb-6 max-w-sm">
              Cliquez sur « + Nouvelle notification » pour commencer.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setFormData(EMPTY_FORM); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle notification</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAskConfirmSend} className="space-y-4 mt-2">

            <div className="space-y-2">
              <Label htmlFor="notif-title">Titre *</Label>
              <Input
                id="notif-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value.slice(0, 50) })}
                placeholder="Promo flash 24h"
                maxLength={50}
                data-testid="notification-title-input"
              />
              <div className="text-[11px] text-slate-400 text-right">{formData.title.length}/50</div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notif-msg">Message *</Label>
              <Textarea
                id="notif-msg"
                rows={2}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, PROMO_MSG_MAX) })}
                placeholder="Promo flash : -20% jusqu'à demain !"
                maxLength={PROMO_MSG_MAX}
                data-testid="notification-message-input"
              />
              <div className="text-[11px] text-slate-400 text-right">{formData.message.length}/{PROMO_MSG_MAX}</div>
              <p className="text-[11px] text-slate-500 leading-snug">
                Apple Wallet n'autorise pas les notifications libres. Le message s'affiche
                <strong> directement sur le pass</strong> et déclenche une notif système iPhone.
                <span className="block mt-0.5 text-slate-400">Limite : {PROMO_MSG_MAX} caractères · 2 envois / 24h.</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label>Cible *</Label>
              <Select
                value={formData.target_type}
                onValueChange={(v) => setFormData({ ...formData, target_type: v, card_id: '', target_id: '' })}
              >
                <SelectTrigger data-testid="notification-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous mes clients</SelectItem>
                  <SelectItem value="card">Clients d'une carte</SelectItem>
                  <SelectItem value="customer">Un client précis</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.target_type === 'card' && (
              <div className="space-y-2">
                <Label>Carte *</Label>
                <Select
                  value={formData.card_id}
                  onValueChange={(v) => setFormData({ ...formData, card_id: v })}
                >
                  <SelectTrigger data-testid="notification-card-select">
                    <SelectValue placeholder="Choisir une carte" />
                  </SelectTrigger>
                  <SelectContent>
                    {cards.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.target_type === 'customer' && (
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select
                  value={formData.target_id}
                  onValueChange={(v) => setFormData({ ...formData, target_id: v })}
                >
                  <SelectTrigger data-testid="notification-customer-select">
                    <SelectValue placeholder="Choisir un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} {c.email ? `· ${c.email}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Preview counter */}
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-100 text-sm" data-testid="preview-count">
              <Smartphone className="w-4 h-4 text-blue-600 shrink-0" />
              <span className="text-blue-900">
                <span className="font-semibold">{previewCount.device_count}</span> appareil{previewCount.device_count > 1 ? 's' : ''}{' '}
                <span className="text-blue-700/80">recevront cette notification</span>
              </span>
              {previewCount.customer_count > 0 && previewCount.device_count === 0 && (
                <span className="text-amber-700 text-xs ml-auto">Aucun client n'a encore ajouté la carte à son wallet</span>
              )}
            </div>

            <DialogFooter className="gap-2 pt-2 flex-col-reverse sm:flex-row">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                type="button" variant="outline"
                onClick={handleTestSelf}
                disabled={testing || !formData.title.trim() || !formData.message.trim()}
                data-testid="test-self-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />}
                Tester sur moi
              </Button>
              <Button
                type="submit" className="bg-blue-600 hover:bg-blue-700"
                disabled={sending}
                data-testid="send-notification-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {previewCount.device_count > 0 ? "Confirmer l'envoi ?" : "Aucun appareil disponible"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {previewCount.device_count > 0 ? (
                <>Cette notification sera envoyée à <strong>{previewCount.device_count}</strong> appareil{previewCount.device_count > 1 ? 's' : ''} client. Cette action est irréversible.</>
              ) : (
                <>Aucun de vos clients n'a encore ajouté votre carte à son wallet. La notification sera <strong>enregistrée</strong> dans l'historique. Vous pourrez la renvoyer plus tard avec le bouton <em>Renvoyer</em>.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSend}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-send-btn"
            >
              {previewCount.device_count > 0 ? 'Envoyer maintenant' : 'Enregistrer quand même'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NotificationsPage;
